import { BookOpenIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { type AgreementStatus, useAgreementStore } from '~/legal/agreement.js';
import { useAuthStore } from '~/store/authStore.js';
import { Lockup } from '~/ui/Lockup.js';
import { MenuTile } from '~/ui/MenuTile.js';
import { Screen } from '~/ui/Screen.js';

/**
 * The one screen a new player must pass, and the one tap (D10, the
 * `lobby-splash` sketch's Welcome).
 *
 * Every install agrees to the Terms and the Privacy Policy before first use,
 * guests included. That tap cannot be skipped, so the fork between playing and
 * learning rides on it for free: both doors agree, the label carries the
 * agreement and the hint carries the fork. Agree and play opens the Arcade
 * (Mitch, 2026-10-01). Agree and learn opens the Cribbage lesson itself, not
 * the Learn shelf, whose Cross lesson is the wrong one for a newcomer.
 *
 * It stands where the home stands: the same ground, the same lockup, the doors
 * in the poster's stack at the lockup's width. The notice's line hangs under
 * the doors, out of the column, so it cannot push them. The two documents are
 * plain links at the foot, and reading them records nothing.
 *
 * Two other shapes, one door each. A device whose agreement predates a
 * material change sees the news in Agree and play's hint. A newcomer on a
 * direct link agrees and goes on to it. An invitation is the exception: it
 * asks for the agreement itself (Agree and join), so the `AgreementGate` in
 * `App.tsx`, which decides which shape stands in front of which route, lets
 * it through.
 */
export function WelcomeScreen({
  status,
}: {
  readonly status: Exclude<AgreementStatus, 'current'>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const here = `${location.pathname}${location.search}${location.hash}`;
  const atRoot = location.pathname === '/';

  /* The record first, so the gate opens on the render the link's navigation
     makes. The registration or the server record follows in the background. */
  const agree = (): void => {
    useAgreementStore.getState().agree();
    void useAuthStore.getState().confirmAgreement();
  };

  const fork = status === 'none' && atRoot;

  return (
    // `relative`: the foot's corner (`home-foot`) is measured from the screen.
    <Screen centered width="poster" ground="suits" className="relative w-full tablet:items-center">
      {/* The home's own stack: centered above the foot on a phone, and the
          poster's column from the tablet breakpoint. */}
      <div className="flex flex-1 flex-col justify-center tablet:flex-none">
        <div className="flex w-full flex-col tablet:w-fit">
          <Lockup className="self-center tablet:self-start" />
          <nav
            className="relative mt-10 flex flex-col gap-3 tablet:mt-11"
            aria-label={t('welcome.doors')}
          >
            {fork ? (
              <>
                <MenuTile
                  size="poster"
                  variant="primary"
                  to="/"
                  suit="spade"
                  label={t('welcome.agreePlay')}
                  hint={t('welcome.agreePlayHint')}
                  onClick={agree}
                />
                <MenuTile
                  size="poster"
                  to="/learn/cribbage"
                  lead={
                    <BookOpenIcon
                      weight="bold"
                      aria-hidden="true"
                      focusable="false"
                      className="size-7 flex-none tablet:size-8"
                    />
                  }
                  label={t('welcome.agreeLearn')}
                  hint={t('welcome.agreeLearnHint')}
                  onClick={agree}
                />
              </>
            ) : (
              /* Back to the same address: the link replaces it, and the
                 gate opens on what the player asked for. */
              <MenuTile
                size="poster"
                variant="primary"
                to={here}
                state={location.state as unknown}
                suit="spade"
                label={t('welcome.agreePlay')}
                hint={status === 'outdated' ? t('welcome.changed') : undefined}
                onClick={agree}
              />
            )}
            <p className="absolute inset-x-0 top-full mt-3 text-center text-sm text-balance text-muted">
              {t(fork ? 'welcome.notice' : 'welcome.noticeOne')}
            </p>
          </nav>
        </div>
      </div>
      <LegalLinks from={here} />
    </Screen>
  );
}

/**
 * Terms and Privacy as two plain text links, at the bottom right. Not ghost
 * chips: a chip's padding held the two words far apart (Mitch, 2026-09-24).
 * No underline at rest; hover draws it and softens the ink one step. Each link
 * keeps the 48px target in both directions, as a link on the legal pages does
 * (`legal.css`), and Back on the page it opens returns here. A newcomer's
 * invitation wears the same foot, because it asks for the same agreement.
 */
export function LegalLinks({ from }: { readonly from: string }): React.JSX.Element {
  const { t } = useTranslation();
  const link =
    'inline-flex min-h-12 min-w-12 items-center justify-center text-sm font-bold text-ink no-underline underline-offset-2 transition-colors hover:text-ink/70 hover:underline';
  return (
    <nav className="home-foot flex justify-end gap-4" aria-label={t('welcome.documents')}>
      <Link to="/terms" state={{ from }} className={link}>
        {t('welcome.terms')}
      </Link>
      <Link to="/privacy" state={{ from }} className={link}>
        {t('welcome.privacy')}
      </Link>
    </nav>
  );
}
